import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CircleCheckIcon,
  Download,
  EditIcon,
  ImageIcon,
  Phone,
  ShoppingBag,
  UserIcon,
  XCircle,
} from "@/components/icons";
import { Badge, Button, Skeleton, confirm, toast } from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { ProductFormSectionLike } from "@/modules/orders/presentation/ui/OrderSection";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { ImageLightboxDialog } from "@/shared/ui/ImageLightboxDialog";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import {
  useCancelCustomJob,
  useCompleteCustomJob,
  useConfirmCustomJob,
  useCustomJobQuery,
  useMarkReadyCustomJob,
  useStartCustomJob,
} from "../api/queries";
import { CustomJobDetailItemsPanel } from "../ui/CustomJobDetailItemsPanel";
import { CustomJobTimelinePanel } from "../ui/CustomJobTimelinePanel";
import { CustomJobWorkflowBar } from "../ui/CustomJobWorkflowBar";
import { getCustomJobTransitionConfirm } from "../ui/custom-job-transition-confirm";
import {
  customJobStatusLabel,
  getCustomJobStatusMeta,
  isCustomJobOverdue,
} from "../ui/job-status";

export function CustomJobDetailPage() {
  const { id } = useParams({ from: "/app/custom-jobs/$id" });
  const nav = useNavigate();
  const canManage = useActionPermission(["custom_jobs:write"]);
  const query = useCustomJobQuery(id);
  const confirmJob = useConfirmCustomJob(id);
  const startJob = useStartCustomJob(id);
  const markReady = useMarkReadyCustomJob(id);
  const completeJob = useCompleteCustomJob(id);
  const cancelJob = useCancelCustomJob(id);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const job = query.data;

  if (query.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6">
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </Main>
      </>
    );
  }

  if (!job) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ບໍ່ພົບງານ
            </h2>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="w-fit rounded-full"
            onClick={() => nav({ to: "/app/custom-jobs" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບ
          </Button>
        </Main>
      </>
    );
  }

  const meta = getCustomJobStatusMeta(job.status);
  const overdue = isCustomJobOverdue(job.dueDate, job.status);
  const editable = job.status === "pending_confirm" || job.status === "queued";

  const runAction = async (
    confirmOptions: Parameters<typeof confirm>[0],
    fn: () => Promise<unknown>,
    successLabel: string,
  ) => {
    const ok = await confirm(confirmOptions);
    if (!ok) return;
    try {
      await fn();
      toast.success(`${successLabel} ສຳເລັດ`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `${successLabel} ບໍ່ສຳເລັດ`);
    }
  };

  return (
    <>
      <Header />
      <Main className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-2xl text-foreground sm:text-3xl">
                {job.jobNumber}
              </h2>
              <Badge
                variant={meta.variant}
                className={cn("rounded-full", meta.className)}
              >
                {customJobStatusLabel(job.status)}
              </Badge>
              {overdue ? (
                <Badge variant="destructive" className="rounded-full">
                  ເກີນກຳນົດ
                </Badge>
              ) : null}
            </div>
            <p className="text-muted-foreground text-sm">
              {job.customerName}
              {job.dueDate ? ` · ນັດ ${formatDateLocal(job.dueDate)}` : ""}
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() =>
                nav({
                  to: "/app/custom-jobs/$id/summary-image",
                  params: { id },
                })
              }
            >
              <Download data-icon="inline-start" />
              ພິມບິນ
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-secondary/40"
              onClick={() => nav({ to: "/app/custom-jobs" })}
            >
              <ArrowLeftIcon data-icon="inline-start" />
              ກັບ
            </Button>
          </div>
        </div>

        <section className="brand-card overflow-hidden p-0">
          <div className="bg-linear-to-br from-secondary/15 via-primary/8 to-background px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-background/80 text-secondary shadow-sm">
                    <UserIcon className="size-5" />
                  </div>
                  <div>
                    <p className="font-medium text-base">{job.customerName}</p>
                    {job.customerPhone ? (
                      <p className="mt-0.5 flex items-center gap-1.5 text-muted-foreground text-sm">
                        <Phone className="size-3.5" />
                        {job.customerPhone}
                      </p>
                    ) : null}
                  </div>
                </div>
                {job.dueDate ? (
                  <p className="flex items-center gap-1.5 text-muted-foreground text-sm">
                    <CalendarIcon className="size-4 shrink-0" />
                    ນັດຮັບ {formatDateLocal(job.dueDate)}
                  </p>
                ) : null}
              </div>
              <div className="text-left sm:text-right">
                <p className="text-muted-foreground text-xs">ຍອດລວມ</p>
                <p className="font-display text-3xl text-secondary tabular-nums">
                  {formatLak(job.total)}
                </p>
                <p className="text-muted-foreground text-xs">ຈ່າຍເມື່ອມາຮັບ</p>
              </div>
            </div>
          </div>

          <CustomJobWorkflowBar status={job.status} />

          {canManage &&
            job.status !== "completed" &&
            job.status !== "cancelled" ? (
            <div className="flex flex-wrap justify-end gap-2 border-secondary/15 border-t px-4 py-3 sm:px-5">
              {job.status === "pending_confirm" ? (
                <>
                  <Button
                    className="rounded-full"
                    disabled={confirmJob.isPending}
                    onClick={() =>
                      runAction(
                        getCustomJobTransitionConfirm("confirm"),
                        () => confirmJob.mutateAsync(),
                        "ຢືນຢັນ",
                      )
                    }
                  >
                    ຢືນຢັນ
                  </Button>
                  {editable ? (
                    <Button variant="outline" className="rounded-full" asChild>
                      <Link to="/app/custom-jobs/$id/edit" params={{ id }}>
                        <EditIcon data-icon="inline-start" />
                        ແກ້ໄຂ
                      </Link>
                    </Button>
                  ) : null}
                </>
              ) : null}
              {job.status === "queued" ? (
                <>
                  <Button
                    className="rounded-full"
                    disabled={startJob.isPending}
                    onClick={() =>
                      runAction(
                        getCustomJobTransitionConfirm("start"),
                        () => startJob.mutateAsync(),
                        "ເລີ່ມເຮັດ",
                      )
                    }
                  >
                    ເລີ່ມເຮັດ
                  </Button>
                  <Button variant="outline" className="rounded-full" asChild>
                    <Link to="/app/custom-jobs/$id/edit" params={{ id }}>
                      <EditIcon data-icon="inline-start" />
                      ແກ້ໄຂ
                    </Link>
                  </Button>
                </>
              ) : null}
              {job.status === "in_progress" ? (
                <Button
                  className="rounded-full"
                  disabled={markReady.isPending}
                  onClick={() =>
                    runAction(
                      getCustomJobTransitionConfirm("mark_ready"),
                      () => markReady.mutateAsync(),
                      "ພ້ອມຮັບ",
                    )
                  }
                >
                  ພ້ອມຮັບ
                </Button>
              ) : null}
              {job.status === "ready" ? (
                <Button
                  className="rounded-full"
                  disabled={completeJob.isPending}
                  onClick={() =>
                    runAction(
                      getCustomJobTransitionConfirm("complete"),
                      () => completeJob.mutateAsync(),
                      "ສົ່ງມອບ+ຮັບເງິນ",
                    )
                  }
                >
                  <CircleCheckIcon data-icon="inline-start" />
                  ສົ່ງມອບ+ຮັບເງິນ
                </Button>
              ) : null}
              <Button
                variant="outline"
                className="rounded-full border-destructive/30 text-destructive"
                disabled={cancelJob.isPending}
                onClick={() =>
                  runAction(
                    getCustomJobTransitionConfirm("cancel", {
                      status: job.status,
                    }),
                    () => cancelJob.mutateAsync({}),
                    "ຍົກເລີກ",
                  )
                }
              >
                <XCircle data-icon="inline-start" />
                ຍົກເລີກ
              </Button>
            </div>
          ) : null}
        </section>

        <div className="grid gap-6 lg:grid-cols-5">
          <div className="flex flex-col gap-6 lg:col-span-2">
            {job.note || job.attachments.length > 0 ? (
              <ProductFormSectionLike
                icon={ImageIcon}
                title="ລາຍລະອຽດງານ"
                description="ໝາຍເຫດ ແລະ ຮູບອ້າງອິງ"
              >
                {job.note ? (
                  <p className="whitespace-pre-wrap rounded-xl bg-muted/30 px-4 py-3 text-sm leading-relaxed">
                    {job.note}
                  </p>
                ) : null}
                {job.attachments.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {job.attachments.map((att, index) => (
                      <button
                        key={att.id}
                        type="button"
                        className="overflow-hidden rounded-2xl ring-1 ring-secondary/15 transition hover:ring-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setLightboxIndex(index)}
                      >
                        <AppImage
                          src={att.imageKey}
                          alt=""
                          className="aspect-square object-cover"
                        />
                      </button>
                    ))}
                  </div>
                ) : null}
              </ProductFormSectionLike>
            ) : null}

            <ProductFormSectionLike
              icon={CalendarIcon}
              title="ໄລຍະເວລາ"
              description="ປະຫວັດຂັ້ນຕອນງານ"
            >
              <CustomJobTimelinePanel job={job} />
            </ProductFormSectionLike>

            {job.cancelNote ? (
              <div className="rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm">
                <p className="font-medium text-destructive">ເຫດຜົນຍົກເລີກ</p>
                <p className="mt-1 text-muted-foreground">{job.cancelNote}</p>
              </div>
            ) : null}
          </div>

          <ProductFormSectionLike
            icon={ShoppingBag}
            title="ລາຍການ"
            description={`${job.items.length} ລາຍການ · ${formatLak(job.total)}`}
            className="lg:col-span-3"
          >
            <CustomJobDetailItemsPanel
              items={job.items}
              subtotal={job.subtotal}
              discountAmount={job.discountAmount}
              total={job.total}
              cogsTotal={job.cogsTotal}
            />
          </ProductFormSectionLike>
        </div>
      </Main>

      {job.attachments.length > 0 ? (
        <ImageLightboxDialog
          open={lightboxIndex !== null}
          onOpenChange={(open) => {
            if (!open) setLightboxIndex(null);
          }}
          images={job.attachments.map((att) => ({ src: att.imageKey }))}
          index={lightboxIndex ?? 0}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </>
  );
}
